import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import {
  decideReferral,
  explainFlags,
  fetchReferralReviewQueue,
  type ReferralReviewRow,
} from "@/lib/referralReview";

const FILTERS = [
  { id: "review", label: "Held for review" },
  { id: "blocked", label: "Blocked" },
  { id: "all", label: "All" },
] as const;

/** Admin queue to audit and override high-risk referral holds with evidence logs. */
export function ReferralReviewQueue() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["id"]>("review");
  const [rows, setRows] = useState<ReferralReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = async (f = filter) => {
    setRows(null);
    setError(null);
    try {
      setRows(await fetchReferralReviewQueue(f));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const decide = async (row: ReferralReviewRow, decision: "approve" | "void") => {
    const note = (notes[row.referral_id] ?? "").trim();
    if (!note) return toast.error("Add a short note explaining the decision");
    setBusy(row.referral_id);
    try {
      await decideReferral({ referralId: row.referral_id, decision, note });
      toast.success(decision === "approve" ? "Referral approved and credited" : "Referral voided");
      setNotes((p) => ({ ...p, [row.referral_id]: "" }));
      await load(filter);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="space-y-4" aria-labelledby="referral-review">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 id="referral-review" className="font-display text-lg font-bold flex items-center gap-2">
            <ShieldAlert className="size-5 text-amber-500" />
            Referral fraud review
          </h2>
          <p className="text-sm text-muted-foreground">
            Audit high-risk referral claims, inspect the evidence log, and override holds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`rounded-md border px-2 py-1 font-mono text-[10px] uppercase transition ${
                filter === f.id
                  ? "border-primary text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
          <Button variant="outline" size="sm" onClick={() => load(filter)}>
            <RefreshCw className="size-4" />
          </Button>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {rows === null ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading queue…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing to review right now.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.referral_id} className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-semibold">
                    Code {r.code} · risk {r.risk_score ?? 0}/100
                    <span className="ml-2 font-mono text-[10px] uppercase text-amber-500">
                      {r.review_status}
                    </span>
                  </p>
                  <p className="font-mono text-[10px] text-muted-foreground">
                    referrer {r.referrer_id.slice(0, 8)} → referred {r.referred_user_id.slice(0, 8)} ·{" "}
                    {new Date(r.created_at).toLocaleString()}
                    {typeof r.validated_amount_cents === "number" && r.validated_amount_cents > 0
                      ? ` · purchase $${(r.validated_amount_cents / 100).toFixed(0)}`
                      : " · no validated purchase yet"}
                  </p>
                </div>
              </div>

              {r.hold_reason && <p className="text-sm text-muted-foreground">{r.hold_reason}</p>}

              <div>
                <h3 className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                  evidence
                </h3>
                <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
                  {(r.signals ?? []).map((s, i) => (
                    <li key={`${s.signal}-${i}`}>
                      <span className="text-foreground">{s.signal}</span> (severity {s.severity})
                      {s.detail ? ` — ${JSON.stringify(s.detail)}` : ""}
                    </li>
                  ))}
                  {(r.signals ?? []).length === 0 &&
                    explainFlags(r.risk_flags).map((f) => <li key={f}>{f}</li>)}
                </ul>
              </div>

              {(r.review_log ?? []).length > 0 && (
                <div>
                  <h3 className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
                    decision log
                  </h3>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {(r.review_log ?? []).map((l, i) => (
                      <li key={i}>
                        <span className="text-foreground">{l.decision ?? "note"}</span>{" "}
                        {l.at ? new Date(l.at).toLocaleString() : ""} — {l.note ?? ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                <Input
                  placeholder="Decision note (required)"
                  value={notes[r.referral_id] ?? ""}
                  onChange={(e) => setNotes((p) => ({ ...p, [r.referral_id]: e.target.value }))}
                  className="flex-1 min-w-[200px]"
                  aria-label={`Decision note for referral ${r.code}`}
                />
                <Button
                  size="sm"
                  onClick={() => decide(r, "approve")}
                  disabled={busy === r.referral_id}
                >
                  {busy === r.referral_id && <Loader2 className="size-4 animate-spin" />} Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => decide(r, "void")}
                  disabled={busy === r.referral_id}
                >
                  Void
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
